import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { KeyRound, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [phase, setPhase] = useState("idle");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) { setErr("Passwords don't match."); return; }
    setPhase("submitting"); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setPhase("done");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Something went wrong.");
      setPhase("error");
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-md px-6 py-14" data-testid="reset-password-page">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm mb-4"
              style={{ color: "var(--text-secondary)" }}>
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="card-surface p-6 sm:p-8">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
               style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}>
            <KeyRound className="w-5 h-5" />
          </div>

          {phase === "done" ? (
            <div data-testid="reset-done-state">
              <h1 className="font-display text-2xl font-extrabold mb-2">Password updated</h1>
              <div className="flex items-start gap-3 p-4 rounded-lg mb-4"
                   style={{ background: "#EFF9F1", border: "1px solid #BEE3C7", color: "#0F5D2E" }}>
                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                <div className="text-sm">Your password has been changed. Sign in with your new password.</div>
              </div>
              <button
                type="button"
                onClick={() => nav("/login")}
                className="btn-primary w-full"
                data-testid="reset-back-to-login-btn"
              >
                Continue to sign in
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-extrabold mb-2">Choose a new password</h1>
              <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
                Pick something you'll remember — at least 6 characters.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="field-label">New password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="field-input"
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    data-testid="reset-password-input"
                    autoComplete="new-password"
                  />
                </div>
                <div>
                  <label className="field-label">Confirm new password</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    className="field-input"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    data-testid="reset-password-confirm-input"
                    autoComplete="new-password"
                  />
                </div>
                {err && (
                  <div className="text-sm px-3 py-2 rounded" style={{ background: "#FDECE7", color: "#B54234" }} data-testid="reset-error">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={phase === "submitting" || !pw1 || !pw2}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="reset-submit-btn"
                >
                  {phase === "submitting" && <Loader2 className="w-4 h-4 animate-spin" />}
                  Update password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
